/**
 * <s-sticky-header> — lives inside the site <header data-site-header>.
 * Toggles `data-scrolled` on that header once the page scrolls, which compacts the
 * header and ends the transparent-over-hero state. Publishes the expanded header
 * height as `--header-height` on the root so the homepage hero can sit beneath it,
 * and adds a bottom margin while compact so content below never jumps.
 */
class SStickyHeader extends HTMLElement {
  #ac;
  #observer;
  #header;
  #expanded = 0;
  #scrolled = false;
  #frame = 0;

  connectedCallback() {
    this.#header = this.closest('header');
    if (!this.#header) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal, passive: true };

    this.#observer = new ResizeObserver(() => this.#sync());
    this.#observer.observe(this.#header);

    window.addEventListener('scroll', () => this.#schedule(), opts);
    window.addEventListener('resize', () => {
      if (!this.#scrolled) this.#expanded = 0;
      this.#sync();
    }, opts);

    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const y = window.scrollY;
    // Hysteresis keeps the header from flickering around the threshold.
    const next = this.#scrolled ? y > 24 : y > 96;
    if (next !== this.#scrolled) {
      this.#scrolled = next;
      this.#header.toggleAttribute('data-scrolled', next);
    }
    this.#sync();
  }

  #sync() {
    const height = this.#header.offsetHeight;
    if (!this.#scrolled && height > this.#expanded) {
      this.#expanded = height;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    }
    const shrink = this.#scrolled ? Math.max(0, this.#expanded - height) : 0;
    this.#header.style.marginBlockEnd = shrink ? `${shrink}px` : '';
  }
}

if (!customElements.get('s-sticky-header')) customElements.define('s-sticky-header', SStickyHeader);
